import { createSignal, For, Show } from "solid-js";
import { Dialog, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Check, Shield } from "@fleet/icons";
import { Button } from "@/components/ui/button";
import { cn } from "@listto/shared";
import { useI18n } from "@/providers/i18n-provider";
import { msg } from "@lingui/core/macro";

interface ChangeRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  memberName: string;
  currentRole: "viewer" | "member" | "admin" | "owner";
  onRoleChange: (role: "viewer" | "member" | "admin" | "owner") => Promise<void>;
}

export function ChangeRoleDialog(props: ChangeRoleDialogProps) {
  const { t } = useI18n();
  const [loading, setLoading] = createSignal(false);

  const ROLES = [
    {
      id: "viewer",
      label: msg`Viewer`,
      description: msg`Can view lists but not edit them`,
    },
    {
      id: "member",
      label: msg`Member`,
      description: msg`Can create and edit their own lists`,
    },
    {
      id: "admin",
      label: msg`Administrator`,
      description: msg`Full control over the organization`,
    },
    {
      id: "owner",
      label: msg`Owner`,
      description: msg`Organization owner`,
    },
  ] as const;

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange} contentClass="max-w-md p-0">
      <DialogHeader size="md">
        <div class="flex items-center gap-3">
          <div class="h-10 w-10 rounded-xl bg-amber-500/10 flex items-center justify-center text-amber-500">
            <Shield class="h-5 w-5" />
          </div>
          <div>
            <DialogTitle class="text-xl font-bold">{t(msg`Change Role`)}</DialogTitle>
            <DialogDescription class="text-xs text-muted-foreground">
              {(() => {
                const name = props.memberName;
                return t(msg`Define permissions for ${name}`);
              })()}
            </DialogDescription>
          </div>
        </div>
      </DialogHeader>

      <div class="p-2 space-y-1">
        <For each={ROLES}>
          {(role) => (
            <button
              type="button"
              disabled={loading()}
              // oxlint-disable-next-line typescript-eslint/promise-function-async
              onClick={() => {
                setLoading(true);
                void (async () => {
                  try {
                    await props.onRoleChange(role.id);
                  } finally {
                    setLoading(false);
                    props.onOpenChange(false);
                  }
                })();
              }}
              class={cn(
                "w-full flex items-center justify-between p-4 rounded-xl hover:bg-accent transition-all text-left group cursor-pointer",
                props.currentRole === role.id && "bg-primary/5 border border-primary/20",
              )}
            >
              <div class="flex-1">
                <div class="font-bold flex items-center gap-2">
                  {t(role.label)}
                  <Show when={props.currentRole === role.id}>
                    <span class="text-[10px] bg-primary/20 text-primary px-1.5 py-0.5 rounded uppercase tracking-wider">
                      {t(msg`Current`)}
                    </span>
                  </Show>
                </div>
                <div class="text-xs text-muted-foreground">{t(role.description)}</div>
              </div>
              <Show when={props.currentRole === role.id}>
                <Check class="h-5 w-5 text-primary" />
              </Show>
            </button>
          )}
        </For>
      </div>

      <div class="p-4 border-t border-border/10 bg-muted/20">
        <Button
          variant="ghost"
          class="w-full h-11 font-bold rounded-xl"
          onClick={() => {
            props.onOpenChange(false);
          }}
        >
          {t(msg`Cancel`)}
        </Button>
      </div>
    </Dialog>
  );
}
